import React from 'react';
import ProcessSection from './ProcessSection';
import { Container, Row, Col } from 'react-bootstrap';
import { 
  Target, 
  Settings, 
  Building2, 
  CheckCircle2, 
  Rocket, 
  RefreshCw 
} from 'lucide-react';
import './FrameworkPage.css';

function FrameworkPage() {
  const differentiators = [
    {
      title: "Business-First Mindset",
      description: "Before writing a single line of code, we immerse ourselves in understanding your business:",
      points: [
        "Comprehensive stakeholder interviews and workshops",
        "Data-driven analysis of your market position",
        "ROI modeling and business case development",
        "Clear alignment of AI initiatives with business KPIs",
        "Strategic roadmapping with measurable milestones"
      ],
      quote: {
        text: "Their focus on our business objectives first, rather than pushing technology solutions, helped us achieve 3x the expected ROI.",
        author: "Fortune 500 Client"
      },
      icon: <Target className="differentiator-icon" />
    },
    {
      title: "End-to-End Expertise",
      description: "We own the entire AI transformation journey, ensuring seamless execution:",
      points: [
        "Strategic planning and technology selection",
        "Data architecture and infrastructure setup",
        "Custom AI model development and training",
        "Integration with existing systems",
        "Production deployment and scaling",
        "Performance monitoring and optimization"
      ],
      quote: {
        text: "Every solution we deliver is backed by our full-stack expertise, eliminating integration headaches and reducing time-to-value by 40%."
      },
      icon: <Settings className="differentiator-icon" />
    },
    {
      title: "Industry-Specific Knowledge",
      description: "Deep vertical expertise that drives meaningful outcomes:",
      points: [
        "Financial Services: Risk modeling, fraud detection, algorithmic trading",
        "Healthcare: Clinical decision support, patient engagement, operational efficiency",
        "Manufacturing: Predictive maintenance, quality control, supply chain optimization",
        "Retail: Personalization, inventory management, demand forecasting",
        "Technology: Product recommendations, content moderation, user experience"
      ],
      quote: {
        text: "Our industry-specific solutions have helped clients achieve up to 45% cost reduction and 60% efficiency gains."
      },
      icon: <Building2 className="differentiator-icon" />
    },
    {
      title: "Proven Methodology",
      description: "Battle-tested approach refined across 160+ enterprise projects:",
      points: [
        "Agile development with 2-week delivery cycles",
        "Continuous validation and stakeholder feedback",
        "Rigorous testing and quality assurance",
        "Comprehensive documentation and knowledge transfer",
        "Risk mitigation and compliance built-in",
        "Regular progress tracking and transparency"
      ],
      quote: {
        text: "Our methodology has delivered a 95% project success rate, significantly above industry average."
      },
      icon: <CheckCircle2 className="differentiator-icon" />
    },
    {
      title: "Future-Ready Solutions",
      description: "We build for tomorrow's challenges, not just today's needs:",
      points: [
        "Scalable cloud-native architecture",
        "Modular and extensible design",
        "Auto-scaling capabilities",
        "Regular updates and improvements",
        "Integration with emerging technologies",
        "Flexible deployment options"
      ],
      quote: {
        text: "Our solutions handle 10x growth without redesign, saving clients millions in future upgrade costs."
      },
      icon: <Rocket className="differentiator-icon" />
    },
    {
      title: "Continuous Support",
      description: "Our commitment extends well beyond deployment:",
      points: [
        "24/7 monitoring and support",
        "Regular performance optimization",
        "Team training and capability building",
        "Quarterly strategy reviews",
        "Technology updates and upgrades",
        "Proactive problem resolution"
      ],
      quote: {
        text: "98% client retention rate, with an average partnership duration of 4+ years."
      },
      icon: <RefreshCw className="differentiator-icon" />
    }
  ];

  return (
    <div className="framework-page">
      <ProcessSection />
      <section className="differentiators-section">
        <Container>
          <h2 className="section-title">Why Our Approach is Effective</h2>
          <Row>
            {differentiators.map((item, index) => (
              <Col md={4} key={index} className="differentiator-item">
                <div className="differentiator-card">
                  <div className="differentiator-icon-wrapper">
                    {item.icon}
                  </div>
                  <h3 className="differentiator-title">{item.title}</h3>
                  <p className="differentiator-description">{item.description}</p>
                  <ul className="differentiator-points">
                    {item.points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                  <blockquote className="differentiator-quote">
                    <p>{item.quote.text}</p>
                    {item.quote.author && (
                      <footer>— {item.quote.author}</footer>
                    )}
                  </blockquote>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </div>
  );
}

export default FrameworkPage; 